// GINI Studio sign-in gate. Rendered when the daemon requires auth
// (`isAuthRequired`) and no valid session cookie exists — see AuthGate in
// client-app.tsx. Native Studio accounts are independent of GINI Code;
// hosted deployments may still use the external issuer proxy.

import { useEffect, useState, type FormEvent } from 'react';
import { useI18n, useT } from '../i18n';
import { loginWithPassword, registerWithInvite } from '../providers/auth';
import { Icon } from './Icon';
import styles from './Welcome.module.css';

interface Props {
  onSignedIn: () => void;
}

export function Welcome({ onSignedIn }: Props) {
  const t = useT();
  const { locale } = useI18n();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auth pages must follow Persian/Arabic RTL when the UI locale is RTL.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const rtl = locale === 'fa' || locale === 'ar';
    document.documentElement.setAttribute('dir', rtl ? 'rtl' : document.documentElement.getAttribute('dir') || 'ltr');
  }, [locale]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (pending) return;
    setError(null);
    setPending(true);
    const result =
      mode === 'login'
        ? await loginWithPassword(email.trim(), password)
        : await registerWithInvite(email.trim(), password, name.trim() || undefined);
    setPending(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    onSignedIn();
  }

  return (
    <div className={styles.shell} data-auth-page="gini-studio">
      <form className={styles.card} onSubmit={handleSubmit}>
        <p className={styles.brand}>{t('app.brand')}</p>
        <h1 className={styles.title}>{t('welcome.title')}</h1>
        <p className={styles.subtitle}>
          {mode === 'register' ? t('welcome.registerInviteHint') : t('welcome.subtitle')}
        </p>

        {mode === 'register' ? (
          <label className={styles.field}>
            <span className={styles.label}>{t('welcome.nameLabel')}</span>
            <input
              className={styles.input}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
            />
          </label>
        ) : null}

        <label className={styles.field}>
          <span className={styles.label}>{t('welcome.emailLabel')}</span>
          <input
            className={styles.input}
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
          />
        </label>

        <label className={styles.field}>
          <span className={styles.label}>{t('welcome.passwordLabel')}</span>
          <input
            className={styles.input}
            type="password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
          />
        </label>

        {error ? (
          <div className={styles.error} role="alert">
            {error}
          </div>
        ) : null}

        <button className={styles.submit} type="submit" disabled={pending}>
          {pending ? (
            <Icon name="spinner" size={14} className={styles.spinner} />
          ) : null}
          {mode === 'login'
            ? pending
              ? t('welcome.loginPending')
              : t('welcome.loginButton')
            : pending
              ? t('welcome.registerPending')
              : t('welcome.registerButton')}
        </button>

        <button
          type="button"
          className={styles.toggle}
          onClick={() => {
            setError(null);
            setMode((m) => (m === 'login' ? 'register' : 'login'));
          }}
        >
          {mode === 'login' ? t('welcome.toggleToRegister') : t('welcome.toggleToLogin')}
        </button>
      </form>
    </div>
  );
}
